import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { captureNetworkError } from "../utils/errorCapture";
import { logClientError } from "../utils/errorLogger";
import storage from "./storage";

declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL?: string;
    readonly DEV?: boolean;
    readonly VITE_APP_VERSION?: string;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// UUID v4 generator
const generateUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getApiBaseUrl = () => {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
    return "http://localhost:3000/api";
  }
  return "https://zeitnahacademy.com/api";
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 15000, // Reduced from 30s to 15s to fail faster and retry
});

/* ── Types ── */
export type AuditLogFilters = {
  action?: string;
  entityType?: string;
  severity?: string;
  page?: number;
  limit?: number;
};

export const getAuditLogs = (filters: AuditLogFilters = {}) =>
  api.get("/audit-logs", {
    params: filters,
  });

/* ── Request Deduplication ── */
const pendingRequests = new Map<string, AbortController>();

const getRequestKey = (config: InternalAxiosRequestConfig) => {
  return `${config.method}:${config.url}:${JSON.stringify(config.data || {})}`;
};

/* ── Request Interceptor ── */
api.interceptors.request.use(async (config) => {
  // If a token refresh is currently in flight, wait for it before dispatching new authenticated requests.
  // This prevents sending requests with known-expired tokens and causing 401 refresh storms.
  // Exclude refresh request itself and unauthenticated login/register routes from waiting on token refresh
  const isRefreshExempt =
    Boolean((config as any)._isRefreshRequest) ||
    Boolean(config.url?.includes('/auth/refresh-token')) ||
    Boolean(config.url?.includes('/auth/login')) ||
    Boolean(config.url?.includes('/auth/register'));

  if (refreshPromise && !isRefreshExempt) {
    try {
      const freshToken = await refreshPromise;
      if (!freshToken) {
        return Promise.reject(new axios.Cancel("Authentication session expired"));
      }
    } catch {
      // Refresh failed permanently; reject immediately without making guaranteed-401 network call
      return Promise.reject(new axios.Cancel("Authentication session expired"));
    }
  }

  // Proactive token refresh: if access token is expired but refresh token exists, refresh before dispatching
  if (!isRefreshExempt && storage.isAccessTokenExpired() && storage.getRefreshToken()) {
    try {
      await getRefreshedToken();
    } catch {
      // If proactive refresh fails, allow request to proceed to standard error handling
    }
  }

  const token = storage.getAccessToken();

  if (token && !(config as any)._isRefreshRequest && !config.url?.includes('/auth/refresh-token')) {
    if (config.headers?.set) {
      config.headers.set("Authorization", `Bearer ${token}`);
    } else {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Generate and attach correlation ID
  const correlationId = generateUUID();
  config.headers["x-correlation-id"] = correlationId;
  (config as any)._correlationId = correlationId;

  // Deduplication logic (prevent accidental rapid double-clicking on mutations)
  // Exclude streaming, heartbeat, progress tracking, auth, and error capture
  const isExcludedFromDeduplication =
    config.method === "get" ||
    config.url?.includes("/courses/heartbeat") ||
    config.url?.includes("/courses/start-stream") ||
    config.url?.includes("/courses/stop-stream") ||
    config.url?.includes("/progress") ||
    config.url?.includes("/auth/") ||
    config.url?.includes("/troubleshoot/");

  if (!isExcludedFromDeduplication && !(config as any).skipDeduplication) {
    const requestKey = getRequestKey(config);
    if (pendingRequests.has(requestKey)) {
      // Abort previous duplicate request
      const controller = pendingRequests.get(requestKey);
      controller?.abort("Duplicate request cancelled");
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(requestKey, controller);
    (config as any)._requestKey = requestKey;
  }

  // Performance: stamp start time for timing
  (config as any)._startTime = Date.now();

  return config;
});

/* ── Robust Single-Flight Token Refresh Logic ── */
let refreshPromise: Promise<string | null> | null = null;
let isRedirecting = false;

export const getRefreshedToken = async (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = storage.getRefreshToken();
  if (!refreshToken) {
    forceLogout();
    return null;
  }

  refreshPromise = (async () => {
    try {
      const res = await api.post(
        "/auth/refresh-token",
        { refreshToken },
        { _isRefreshRequest: true } as any,
      );

      const newAccessToken = res.data?.accessToken || res.data?.token;
      const newRefreshToken = res.data?.refreshToken;
      const newExpiresAt = res.data?.sessionExpiresAt;

      // Persist the new tokens via storage BEFORE releasing waiting requests
      if (newAccessToken) {
        storage.setAccessToken(newAccessToken);

        // Notify socket and other real-time consumers that a fresh token is available
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("zeitnah:auth:token-refreshed", {
              detail: { token: newAccessToken },
            })
          );
        }
      }
      if (newRefreshToken) {
        storage.setRefreshToken(newRefreshToken);
      }
      if (newExpiresAt) {
        storage.setSessionExpiresAt(newExpiresAt);
      }

      return newAccessToken || null;
    } catch (refreshError) {
      forceLogout();
      throw refreshError;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/* ── Response Interceptor ── */

// Friendly error mapping
const getFriendlyErrorMessage = (error: AxiosError, isOffline: boolean): string => {
  if (isOffline) {
    return "You appear to be offline. Please check your internet connection.";
  }
  if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
    return "The request timed out. The server might be busy or your network is slow.";
  }
  if (error.code === "ERR_NETWORK") {
    return "Unable to connect to the server. Please check your internet connection or try again later.";
  }
  if (error.message === "Duplicate request cancelled" || error.code === "ERR_CANCELED") {
    return "Request cancelled to prevent duplicates.";
  }

  const status = error.response?.status;
  const data = error.response?.data as any;
  const backendMsg = data?.message;

  if (status) {
    switch (status) {
      case 400: return backendMsg || "Invalid request. Please check your input.";
      case 401: return backendMsg || "Your session has expired. Please log in again.";
      case 403: return backendMsg || "You do not have permission to perform this action.";
      case 404: return backendMsg || "The requested resource was not found.";
      case 408: return "The request took too long. Please try again.";
      case 409: return backendMsg || "There was a conflict with your request (e.g., duplicate entry).";
      case 422: return backendMsg || "Validation failed. Please verify your data.";
      case 429: return backendMsg || "You are making too many requests. Please wait a moment and try again.";
      case 500: return "We encountered an internal server error. Our team has been notified.";
      case 502: return "Bad Gateway. The server is currently unreachable.";
      case 503: return "Service is temporarily unavailable due to maintenance or overload.";
      case 504: return "Gateway timeout. The server took too long to respond.";
      default: return backendMsg || `Unexpected error occurred (Code: ${status}).`;
    }
  }

  // Check for invalid JSON parsing if status isn't available
  if (error.message.includes("Unexpected token") || error.message.includes("JSON")) {
    return "Received an invalid response from the server.";
  }

  return "Something went wrong. Please try again.";
};

api.interceptors.response.use(
  (response) => {
    // Clear deduplication
    const requestKey = (response.config as any)?._requestKey;
    if (requestKey) {
      pendingRequests.delete(requestKey);
    }

    // Log slow requests (all environments)
    const startTime = (response.config as any)?._startTime;
    if (startTime) {
      const duration = Date.now() - startTime;
      if (duration > 5000) {
        console.warn(`[API] Slow request: ${response.config.url} took ${duration}ms`);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as any;

    // Clear deduplication tracking
    if (config?._requestKey) {
      pendingRequests.delete(config._requestKey);
    }

    // Don't retry if request was cancelled (duplicate)
    if (error.code === "ERR_CANCELED" || error.message === "Duplicate request cancelled") {
      return Promise.reject({ friendlyMessage: getFriendlyErrorMessage(error, false), isCancelled: true });
    }

    const isNetworkOr5xx = !error.response || (error.response?.status >= 500);
    const isOffline = !navigator.onLine;
    const isTroubleshootRequest = config?.url?.includes('/troubleshoot/');
    const isRefreshRequest = config?.url?.includes('/auth/refresh-token') || config?._isRefreshRequest;

    // Retry logic:
    // Only retry idempotent operations (GET, HEAD, OPTIONS) on network failures or 5xx gateway errors.
    // NEVER automatically retry non-idempotent operations (POST, PUT, DELETE, PATCH)
    // such as payments, enrollments, submissions, or orders, unless explicitly marked config.retryable === true
    // Never retry troubleshoot or refresh-token endpoints to prevent infinite cascades / retries
    const method = (config?.method || "get").toLowerCase();
    const isIdempotentMethod = ["get", "head", "options"].includes(method);
    const isExplicitlyRetryable =
      Boolean(config?.retryable) ||
      config?.url?.includes("/courses/heartbeat");

    const shouldRetry =
      !isOffline &&
      isNetworkOr5xx &&
      config &&
      !config._retried &&
      !isTroubleshootRequest &&
      !isRefreshRequest &&
      (isIdempotentMethod || isExplicitlyRetryable);

    if (shouldRetry) {
      config._retried = true;
      console.warn(`[API] Retrying idempotent request ${config.url} due to ${error.code || error.response?.status}`);
      await new Promise((resolve) => setTimeout(resolve, 1500)); // wait 1.5s
      return api(config); // retry
    }

    // Handle 401 — attempt token refresh before logging out
    // Guard against infinite 401 retry storms: never refresh if this request already attempted a 401 retry,
    // or if the request was an auth action (logout, login, register, refresh)
    const isAuthRoute =
      config?.url?.includes('/auth/logout') ||
      config?.url?.includes('/auth/login') ||
      config?.url?.includes('/auth/register') ||
      config?.url?.includes('/auth/refresh-token');

    if (error.response?.status === 401 && config && !config._isRefreshRequest && !isAuthRoute) {
      if (config._retried401) {
        console.warn(`[API] 401 persisted after token refresh for ${config.url}. Rejecting without further retries.`);
        return Promise.reject(error);
      }
      config._retried401 = true;

      try {
        const freshToken = await getRefreshedToken();
        if (!freshToken) {
          return Promise.reject(error);
        }

        if (config.headers?.set) {
          config.headers.set("Authorization", `Bearer ${freshToken}`);
        } else {
          config.headers.Authorization = `Bearer ${freshToken}`;
        }

        return api(config);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Capture network errors for troubleshoot reports
    // (skip capturing failures from the troubleshoot endpoint itself)
    if (config?.url && !config.url.includes('/troubleshoot/')) {
      captureNetworkError({
        method: (config.method || 'UNKNOWN').toUpperCase(),
        url: config.url || '',
        status: error.response?.status || 0,
        message:
          (error.response?.data as any)?.message ||
          error.message ||
          'Network error',
      });
    }

    // Determine friendly message
    const friendlyMessage = getFriendlyErrorMessage(error, isOffline);
    const requestDuration = config?._startTime ? Date.now() - config._startTime : undefined;
    const isTimeout = error.code === "ECONNABORTED" || error.message?.includes("timeout");

    // Only log genuine server/network errors to the backend telemetry
    // Do NOT log expected unauthenticated session checks (/auth/me) or normal token expirations
    const isExpectedAuth401 =
      error.response?.status === 401 &&
      (config?.url?.includes('/auth/me') ||
        config?.url?.includes('/auth/refresh-token') ||
        config?.url?.includes('/auth/login') ||
        config?.url?.includes('/notifications/push-token'));

    const shouldLogTelemetry =
      !isExpectedAuth401 &&
      !isOffline &&
      !isTroubleshootRequest &&
      ((error.response && error.response.status >= 500) ||
        !error.response ||
        (config?.url?.includes('/auth/') && error.response?.status !== 401));

    if (shouldLogTelemetry) {
      // Fire and forget logging
      logClientError({
        message: error.message,
        code: error.code,
        correlationId: config?._correlationId,
        apiUrl: config?.url,
        httpStatus: error.response?.status,
        responseBody: error.response?.data,
        stack: error.stack,
      });
    }

    // Reject with a structured object that UI components can easily read
    return Promise.reject({
      ...error,
      friendlyMessage,
      correlationId: config?._correlationId,
      isOffline,
      isTimeout,
      requestDuration,
    });
  },
);

/** Clear all auth tokens and navigate to login safely without state desync */
function forceLogout() {
  storage.clearAuth(); // This already dispatches zeitnah:auth:logout

  if (typeof window !== "undefined") {
    const publicPaths = [
      "/login",
      "/register",
      "/verify-login-otp",
      "/verify-register-otp",
      "/privacy-policy",
      "/privacy",
      "/terms-of-service",
      "/terms",
      "/support",
      "/help",
      "/about",
      "/u/",
      "/profile/u/",
    ];
    const isPublic = publicPaths.some((p) =>
      window.location.pathname.startsWith(p),
    );

    if (!isPublic && !isRedirecting) {
      isRedirecting = true;
      // Cleanly replace window location to avoid React Router context desync
      window.location.replace("/login");

      // Reset after navigation
      setTimeout(() => {
        isRedirecting = false;
      }, 1500);
    }
  }
}

export default api;
